import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const F = ({ label, children }) => <label className="block"><span className="text-xs text-[#8C827A]">{label}</span><div className="mt-1">{children}</div></label>;

export const TownEdit = ({ town, countries, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (town) setF({ town: town.town || "", region: town.region || "", province: town.province || "", country: town.country || "Italy", number: town.number ?? "", notes: town.notes || "", shortlisted: false }); }, [town]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const locked = !!town?.guide_id;
  const adding = town && !town.id;
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, number: f.number === "" ? null : Number(f.number) };
      if (adding) await api.post("/towns", body);
      else { delete body.shortlisted; await api.put(`/towns/${town.id}`, body); }
      toast.success(adding ? `${f.town.trim()} added to the list` : `${f.town} updated`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!town} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-[#FAF7F2] max-w-md" data-testid="town-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#3B1B48]">{adding ? "Add a town" : `Edit ${town?.town}`}</DialogTitle>
          {locked && <DialogDescription>This town has a guide (No. {town.issue_number}), so its name and country stay as they are.</DialogDescription>}
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><F label="Town"><Input data-testid="town-edit-name" value={f.town || ""} onChange={set("town")} disabled={locked} className="rounded-none bg-white" /></F></div>
          <F label="Number (#)"><Input data-testid="town-edit-number" type="number" min="1" value={f.number} onChange={set("number")} className="rounded-none bg-white" /></F>
          <F label="Country">
            <select data-testid="town-edit-country" value={f.country || ""} onChange={set("country")} disabled={locked} className="h-9 w-full bg-white border border-input px-2 text-sm rounded-none disabled:opacity-50">
              {countries.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </F>
          <F label="Province"><Input data-testid="town-edit-province" value={f.province || ""} onChange={set("province")} className="rounded-none bg-white" /></F>
          <F label="Region"><Input data-testid="town-edit-region" value={f.region || ""} onChange={set("region")} className="rounded-none bg-white" /></F>
          <div className="col-span-2"><F label="Private note (only you see this)"><Input data-testid="town-edit-notes" maxLength={300} value={f.notes || ""} onChange={set("notes")} placeholder="e.g. visited 2025, great markets" className="rounded-none bg-white" /></F></div>
        </div>
        {adding && <label className="flex items-center gap-2 text-sm text-[#3B1B48] cursor-pointer" data-testid="town-edit-shortlist"><input type="checkbox" checked={!!f.shortlisted} onChange={(e) => setF({ ...f, shortlisted: e.target.checked })} className="accent-[#3B1B48] w-4 h-4" />Add it to the shortlist</label>}
        <p className="text-xs text-[#8C827A]">{adding ? "The town is placed on the map automatically." : "Changing the name, province, region or country re-places the town on the map."}</p>
        <DialogFooter>
          <Button variant="ghost" data-testid="town-edit-cancel" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button data-testid="town-edit-save" onClick={save} disabled={busy || !(f.town || "").trim()} className="rounded-none">{busy ? "Saving…" : adding ? "Add town" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
